/**
 * Task #19 — Customer-facing quiz player. Cookie-authed. Same shape as
 * TrainingQuizPage but talks to /v1/learn/quiz endpoints.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { QuizAttemptResult, QuizQuestionPublic, QuizSubmission } from '@xsc/shared';
import { ApiError } from '../lib/api-client.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Button } from '../components/ui/button.js';
import { Badge } from '../components/ui/badge.js';

async function learnFetch(method: string, path: string, body?: unknown): Promise<unknown> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let parsed: unknown = null;
  if (text) { try { parsed = JSON.parse(text); } catch { parsed = text; } }
  if (!res.ok) throw new ApiError(res.status, parsed, `${method} ${path} failed`);
  return parsed;
}

export function LearnerQuizPage(): JSX.Element {
  const [questions, setQuestions] = useState<QuizQuestionPublic[] | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<(QuizAttemptResult & { certificate?: unknown }) | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    learnFetch('GET', '/v1/learn/quiz')
      .then((d) => setQuestions(d as QuizQuestionPublic[]))
      .catch((e: Error) => setErr(e.message));
  }, []);

  if (err) return <p className="p-6 text-danger">Could not load the quiz.</p>;
  if (!questions) return <p className="p-6 text-ink-muted">Loading…</p>;
  if (questions.length === 0) {
    return <p className="p-6 text-ink-muted">This module has no quiz.</p>;
  }

  const answered = questions.filter((q) => answers[q.id]).length;
  const canSubmit = answered === questions.length && !submitting && !result;

  async function submit(): Promise<void> {
    setSubmitting(true);
    try {
      const body: QuizSubmission = {
        answers: questions!.map((q) => ({
          question_id: q.id,
          selected_option_id: answers[q.id] ?? '',
        })),
      };
      const r = (await learnFetch('POST', '/v1/learn/quiz/submit', body)) as
        QuizAttemptResult & { certificate?: unknown };
      setResult(r);
    } catch {
      setErr('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    const cert = result.certificate as
      | { certificate_ref: string; earned_at: string }
      | null
      | undefined;
    return (
      <div className="max-w-3xl mx-auto p-6 space-y-4">
        <h1 className="text-2xl font-semibold">Quiz results</h1>
        <Card>
          <CardBody className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-3xl font-semibold">{Math.round(result.score)}%</div>
              <div className="text-sm text-ink-muted">
                {result.points_earned} of {result.points_possible} points · pass ≥ {result.passing_score}%
              </div>
            </div>
            {result.passed ? <Badge tone="success">Passed</Badge> : <Badge tone="danger">Did not pass</Badge>}
          </CardBody>
        </Card>
        {cert && (
          <Card>
            <CardHeader><CardTitle>Certificate</CardTitle></CardHeader>
            <CardBody>
              <p className="text-sm">
                Reference: <code className="font-mono">{cert.certificate_ref}</code>
              </p>
              <p className="text-xs text-ink-muted">Issued {new Date(cert.earned_at).toLocaleString()}.</p>
            </CardBody>
          </Card>
        )}
        <Link to="/learn/module"><Button variant="secondary">Back to module</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-4">
      <h1 className="text-2xl font-semibold">Module quiz</h1>
      <p className="text-sm text-ink-muted">{answered} / {questions.length} answered</p>
      <div className="space-y-3">
        {questions.map((q, i) => (
          <Card key={q.id}>
            <CardHeader><CardTitle>{i + 1}. {q.question}</CardTitle></CardHeader>
            <CardBody>
              <fieldset className="space-y-2">
                <legend className="sr-only">{q.question}</legend>
                {q.options.map((o) => {
                  const id = `q-${q.id}-${o.id}`;
                  return (
                    <label key={o.id} htmlFor={id}
                      className="flex items-start gap-2 rounded border border-border px-3 py-2 cursor-pointer hover:bg-surface-alt">
                      <input id={id} type="radio" name={q.id} className="mt-1"
                        checked={answers[q.id] === o.id}
                        onChange={() => setAnswers((p) => ({ ...p, [q.id]: o.id }))} />
                      <span className="text-sm">{o.option_text}</span>
                    </label>
                  );
                })}
              </fieldset>
            </CardBody>
          </Card>
        ))}
      </div>
      <div className="flex justify-end gap-2">
        <Link to="/learn/module"><Button variant="secondary">Cancel</Button></Link>
        <Button disabled={!canSubmit} onClick={submit}>{submitting ? 'Submitting…' : 'Submit quiz'}</Button>
      </div>
    </div>
  );
}
